import { captureException } from "@sentry/browser";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getGpsLocation } from "@/helpers/location";
import useAnalytics from "@/hooks/use-analytics";
import useCurrentLocation from "@/hooks/use-current-location";

const STORAGE_KEY = "location_permission_modal";

interface FeatureProps {
  children: React.ReactNode;
  icon: string;
}

function Feature({ children, icon }: FeatureProps) {
  return (
    <li className="flex items-center gap-2.5 rounded-[5px] bg-white/10 p-2.5 shadow-0-0-8 shadow-black/25">
      <p className="w-5 text-center">{icon}</p>
      <p className="text-14/[1.5em]">{children}</p>
    </li>
  );
}

export default function LocationPermission() {
  const { track } = useAnalytics();
  const { toExploreUrl } = useCurrentLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const handleSkip = () => {
    track("Location Sharing Skipped");
    setIsOpen(false);
  };

  const handleUseLocation = async () => {
    setIsOpen(false);

    try {
      const newLocation = await getGpsLocation();
      if (newLocation?.location) {
        const [lat, lng] = newLocation.location.split(",").map(Number);
        track("User Location Updated", { lat, lng });
        navigate({ replace: true, to: toExploreUrl(false, newLocation) });
      }
    } catch (error) {
      captureException(error);
    }
  };

  useEffect(() => {
    if (!("permissions" in navigator)) {
      return;
    }
    if (sessionStorage.getItem(STORAGE_KEY)) {
      return;
    }

    navigator.permissions.query({ name: "geolocation" }).then(({ state }) => {
      if (state === "prompt") {
        setTimeout(() => {
          setIsOpen(true);
        }, 100);
      }
    });

    sessionStorage.setItem(STORAGE_KEY, new Date().toString());
  }, []);

  return (
    <Dialog onOpenChange={setIsOpen} open={isOpen}>
      <DialogContent
        className="gap-0 rounded-t-[15px] bg-[url(/images/main-bg.jpg)] bg-cover bg-top bg-no-repeat text-white shadow-0-0-8 shadow-black/70 md:w-sm md:rounded-b-[15px]"
        overlayClassName="bg-black/25 md:bg-transparent md:items-center"
      >
        <DialogHeader className="flex flex-col gap-4 px-[57px] py-5">
          <DialogTitle className="text-center font-semibold text-16/[1.5em]">
            Find closest attractions
          </DialogTitle>
          <DialogDescription className="text-center text-14/[1.5em] text-white">
            Turn on location services to access these features 👇
          </DialogDescription>
        </DialogHeader>
        <ul className="flex flex-col items-stretch gap-2.5 px-5">
          <Feature icon="📍">Find experiences near me</Feature>
          <Feature icon="🛝">Learn about new openings</Feature>
          <Feature icon="🗺️">Find awesome stuff when traveling</Feature>
        </ul>
        <DialogFooter className="flex-col items-stretch gap-2.5 p-5 sm:flex-col">
          <Button
            className="h-[45px] rounded-[23px]"
            onClick={handleUseLocation}
            variant="cta"
          >
            Use my location
          </Button>
          <Button
            className="h-10 font-medium text-14 hover:underline"
            onClick={handleSkip}
          >
            Maybe later
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
